import {
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  KeyboardEvent,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { toast } from '@/lib/caixaToast';
import { ScanBarcode, Hash, Banknote, Coins, MessageCircle } from 'lucide-react';
import { ProdutoCaixa } from '@caixa/lib/api';
import { formatBRL } from '@caixa/lib/format';
import { textIncludesSearch } from '@caixa/lib/searchText';
import { numberToMoneyMask, parseMoneyMask } from '@caixa/lib/moneyMask';
import { MoneyInput } from '@caixa/components/MoneyInput';
import { OBSERVACAO_ITEM_MAX_LEN, normalizeItemObservacao } from '@caixa/types';
import { labelQuantidadeCaixa, unidadeVendaPermiteDecimal } from '@/lib/unidadeVenda';

function roundQuantidadeDecimal(value: number): number {
  return Math.round(value * 1000) / 1000;
}

function formatQuantidadeDecimalDigits(digits: string): string {
  if (digits === '') return '';
  if (digits.length <= 4) {
    const inteiro = digits.slice(0, 1) || '0';
    const decimal = digits.slice(1).padEnd(3, '0');
    return `${String(parseInt(inteiro, 10) || 0)}.${decimal}`;
  }
  const inteiro = digits.slice(0, -3).replace(/^0+(?=\d)/, '') || '0';
  const decimal = digits.slice(-3);
  return `${inteiro}.${decimal}`;
}

function parseQuantidadeDecimalDigits(digits: string): number {
  const formatted = formatQuantidadeDecimalDigits(digits);
  if (formatted === '') return 0;
  const value = Number(formatted);
  return Number.isFinite(value) ? value : 0;
}

export interface SearchProdutoRef {
  focusSearch: () => void;
}

interface SearchProdutoProps {
  produtos: ProdutoCaixa[];
  produtosLoaded?: boolean;
  onAdd: (item: {
    cod: number;
    nome: string;
    quantidade: number;
    precoUnitario: number;
    permiteDecimal?: boolean;
    observacao?: string;
    promocao_id?: number;
    itens_escolhidos?: Array<{ produto_id: number; nome_produto: string; quantidade: number; preco_unitario: number }>;
  }) => void;
  /** Quando o produto é uma promo configurável, chamado em vez de onAdd (abre modal no pai) */
  onSelectPromoConfiguravel?: (produto: ProdutoCaixa) => void;
  /** Promoção por categoria: abre modal de escolha de produtos */
  onSelectPromoCategoria?: (produto: ProdutoCaixa) => void;
  disabled?: boolean;
}

export const SearchProduto = forwardRef<SearchProdutoRef, SearchProdutoProps>(
  function SearchProduto(
    { produtos, produtosLoaded = false, onAdd, onSelectPromoConfiguravel, onSelectPromoCategoria, disabled },
    ref
  ) {
    const searchRef = useRef<HTMLInputElement>(null);
    const quantidadeRef = useRef<HTMLInputElement>(null);
    const [query, setQuery] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [selectedProduct, setSelectedProduct] = useState<ProdutoCaixa | null>(null);
    const [quantidade, setQuantidade] = useState(1);
    const [quantidadeStr, setQuantidadeStr] = useState('1');
    const [quantidadeDigits, setQuantidadeDigits] = useState('1');
    const [precoUnitarioStr, setPrecoUnitarioStr] = useState('');
    const [observacao, setObservacao] = useState('');

    const resetQuantidade = (permiteDec: boolean) => {
      if (permiteDec) {
        setQuantidade(0);
        setQuantidadeStr('');
        setQuantidadeDigits('');
        return;
      }
      setQuantidade(1);
      setQuantidadeStr('1');
      setQuantidadeDigits('1');
    };

    const focusSearch = () => {
      setSelectedProduct(null);
      resetQuantidade(false);
      setPrecoUnitarioStr('');
      setObservacao('');
      setQuery('');
      setSelectedIndex(0);
      searchRef.current?.focus();
    };

    useImperativeHandle(ref, () => ({ focusSearch }), []);

    const filtered = useMemo(() => {
      if (!query.trim()) return [];
      const q = query.trim();
      return produtos.filter(
        (p) => textIncludesSearch(p.nome, q) || String(p.id).includes(q)
      );
    }, [produtos, query]);

    useEffect(() => {
      if (selectedProduct) {
        setPrecoUnitarioStr(numberToMoneyMask(selectedProduct.preco_venda ?? 0));
      }
    }, [selectedProduct]);

    useEffect(() => {
      setSelectedIndex(0);
    }, [query]);

    useLayoutEffect(() => {
      if (disabled || !selectedProduct || !unidadeVendaPermiteDecimal(selectedProduct.unidade_venda)) return;
      const input = quantidadeRef.current;
      if (!input || input.disabled || input.readOnly) return;
      input.focus();
      input.select();
    }, [selectedProduct, disabled]);

    const precoUnitario = parseMoneyMask(precoUnitarioStr);
    const total = quantidade * precoUnitario;
    const hasProduct = selectedProduct !== null;
    const permiteDecimal =
      hasProduct && unidadeVendaPermiteDecimal(selectedProduct?.unidade_venda);
    const quantidadeLabel = hasProduct
      ? labelQuantidadeCaixa(selectedProduct?.unidade_venda)
      : 'Quantidade';

    /** Identifica promoção por categoria (tipo explícito ou grupos vindos da API). Nunca tratar como produto comum. */
    const produtoEhPromocaoCategoria = (p: ProdutoCaixa): boolean => {
      if (p.promocao_tipo === 'categoria') return true;
      if (p.promocao_tipo === 'fixa' || p.promocao_tipo === 'configuravel') return false;
      const gr = p.promocao_categorias_grupos;
      return Array.isArray(gr) && gr.length > 0;
    };

    /** Promo configurável / categoria: abre modal no pai. Retorna true se tratou (não seguir para onAdd). */
    const handlePromoEspecial = (p: ProdutoCaixa): boolean => {
      if (p.promocao_tipo === 'configuravel' && onSelectPromoConfiguravel) {
        onSelectPromoConfiguravel(p);
        return true;
      }
      if (produtoEhPromocaoCategoria(p)) {
        if (!onSelectPromoCategoria) {
          toast.error('Não foi possível abrir a seleção da promoção por categoria.');
          return true;
        }
        const gr = p.promocao_categorias_grupos ?? [];
        if (gr.length === 0) {
          toast.error('Esta promoção não tem categorias configuradas. Edite no Marketing.');
          return true;
        }
        if (gr.some((g) => g.quantidade < 1)) {
          toast.error('Quantidade inválida em alguma categoria da promoção.');
          return true;
        }
        /** Lista vazia por divergência de nome de categoria era comum — backend list-caixa compara sem case; aviso só informativo. */
        if (gr.some((g) => !g.produtos || g.produtos.length === 0)) {
          toast.warning(
            'Há categoria(s) sem produtos no cadastro ou com nome diferente da promoção. Ajuste produtos/categorias na gestão.',
          );
        }
        onSelectPromoCategoria(p);
        setSelectedProduct(null);
        resetQuantidade(false);
        setQuery('');
        setPrecoUnitarioStr('');
        setObservacao('');
        searchRef.current?.focus();
        return true;
      }
      return false;
    };

    const tryAdd = () => {
      if (!selectedProduct) return;
      if (handlePromoEspecial(selectedProduct)) return;
      if (permiteDecimal ? quantidade <= 0 : quantidade < 1) return;
      const obs = normalizeItemObservacao(observacao);
      onAdd({
        cod: selectedProduct.id,
        nome: selectedProduct.nome,
        quantidade: permiteDecimal ? roundQuantidadeDecimal(quantidade) : Math.round(quantidade),
        precoUnitario,
        permiteDecimal,
        ...(obs ? { observacao: obs } : {}),
      });
      setSelectedProduct(null);
      resetQuantidade(false);
      setQuery('');
      setPrecoUnitarioStr('');
      setObservacao('');
      searchRef.current?.focus();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        focusSearch();
        return;
      }
      if (e.key === 'Enter') {
        if (hasProduct) {
          e.preventDefault();
          tryAdd();
          return;
        }
        if (filtered.length > 0) {
          e.preventDefault();
          const sel = filtered[selectedIndex];
          if (sel) {
            /** Um Enter: promo categoria/configurável abre o modal na hora (mesmo fluxo do botão Adicionar). */
            if (handlePromoEspecial(sel)) {
              return;
            }
            setSelectedProduct(sel);
            const dec = unidadeVendaPermiteDecimal(sel.unidade_venda);
            resetQuantidade(dec);
            setPrecoUnitarioStr(numberToMoneyMask(sel.preco_venda ?? 0));
            setObservacao('');
            setQuery(sel.nome);
          }
          return;
        }
      }
      if (!hasProduct && filtered.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedIndex((i) => (i + 1) % filtered.length);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedIndex((i) => (i - 1 + filtered.length) % filtered.length);
        }
      }
    };

    return (
      <div className="relative flex w-full min-w-0 flex-col gap-2.5 overflow-x-hidden overflow-y-visible rounded-lg border border-zinc-700 bg-[#242424] p-3 lg:gap-4 lg:overflow-hidden lg:p-4">
        <h2 className="shrink-0 text-lg font-semibold text-white">Produto</h2>

        {/* Busca: ícone + campo (sem label de texto) */}
        <div className="flex w-full min-w-0 items-center gap-2">
          <ScanBarcode className="h-5 w-5 shrink-0 text-white" aria-hidden />
          <input
            id="caixa-produto-busca"
            ref={searchRef}
            type="text"
            placeholder="Buscar por nome ou código..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedProduct(null);
              setObservacao('');
            }}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            autoFocus
            aria-label="Buscar produto"
            className="box-border min-h-[40px] w-full min-w-0 flex-1 rounded-md border-2 border-zinc-600 bg-[#333333] px-3 py-2 text-sm text-white outline-none placeholder-zinc-400 focus:border-white focus:ring-1 focus:ring-white/80 lg:min-h-[44px] lg:px-4 lg:py-3 lg:text-base"
          />
        </div>

        {produtosLoaded && produtos.length === 0 && !hasProduct && (
          <p className="py-2 text-sm text-zinc-500">Nenhum produto cadastrado.</p>
        )}
        {filtered.length > 0 && !hasProduct && (
          <ul className="max-h-48 w-full min-w-0 overflow-y-auto overflow-x-hidden rounded-md border border-zinc-600">
            {filtered.slice(0, 20).map((p, i) => (
              <li
                key={p.id}
                className={`flex cursor-pointer items-center justify-between gap-2 px-3 py-2 ${
                  i === selectedIndex
                    ? 'bg-[#333333] text-white ring-1 ring-inset ring-white/25'
                    : 'bg-[#242424] text-zinc-200 hover:bg-[#333333]'
                }`}
                onMouseEnter={() => setSelectedIndex(i)}
                onClick={() => {
                  if (handlePromoEspecial(p)) {
                    return;
                  }
                  setSelectedProduct(p);
                  resetQuantidade(unidadeVendaPermiteDecimal(p.unidade_venda));
                  setPrecoUnitarioStr(numberToMoneyMask(p.preco_venda ?? 0));
                  setObservacao('');
                  setQuery(p.nome);
                }}
              >
                <span className="min-w-0 truncate">{p.nome}</span>
                <span className="shrink-0 text-sm text-zinc-400">{formatBRL(p.preco_venda ?? 0)}</span>
              </li>
            ))}
          </ul>
        )}

        {hasProduct ? (
          <div className="grid w-full min-w-0 grid-cols-[1.25rem_6.75rem_minmax(0,1fr)] items-start gap-x-2 lg:flex lg:gap-2">
            <MessageCircle className="mt-2.5 h-5 w-5 shrink-0 text-white" aria-hidden />
            <label
              htmlFor="caixa-produto-obs"
              className="mt-2.5 truncate text-sm text-zinc-300 lg:w-28 lg:shrink-0"
            >
              Descrição:
            </label>
            <textarea
              id="caixa-produto-obs"
              rows={2}
              maxLength={OBSERVACAO_ITEM_MAX_LEN}
              value={observacao}
              onChange={(e) => setObservacao(e.target.value.slice(0, OBSERVACAO_ITEM_MAX_LEN))}
              className="box-border min-h-[40px] w-full min-w-0 resize-y rounded-md border border-zinc-600 bg-[#333333] px-2.5 py-2 text-sm text-white outline-none focus:border-white focus:ring-1 focus:ring-white/80 lg:min-h-[44px] lg:flex-1 lg:px-3 lg:py-2.5"
            />
          </div>
        ) : null}

        {/*
          Grid mobile: ícone | label (largura fixa) | campo
          Alinha a coluna dos inputs; quantidade menor; unitário/total iguais.
          Desktop (lg): layout flex original com ícone + label w-28 + campo.
        */}
        <div className="flex w-full min-w-0 flex-col gap-2.5 lg:gap-3">
          <div className="grid w-full min-w-0 grid-cols-[1.25rem_6.75rem_minmax(0,1fr)] items-center gap-x-2 lg:flex lg:gap-2">
            <Hash className="h-5 w-5 shrink-0 text-white" aria-hidden />
            <label
              htmlFor="caixa-produto-qtd"
              className="truncate text-sm text-zinc-300 lg:w-28 lg:shrink-0"
            >
              {quantidadeLabel}:
            </label>
            <input
              id="caixa-produto-qtd"
              ref={quantidadeRef}
              type="text"
              inputMode="numeric"
              value={hasProduct ? quantidadeStr : ''}
              readOnly={!hasProduct}
              onChange={(e) => {
                if (!hasProduct) return;
                const raw = e.target.value;
                if (permiteDecimal) {
                  const digits = raw.replace(/\D/g, '');
                  setQuantidadeDigits(digits);
                  setQuantidadeStr(formatQuantidadeDecimalDigits(digits));
                  setQuantidade(parseQuantidadeDecimalDigits(digits));
                } else {
                  if (/[,.]/.test(raw) || /[^0-9]/.test(raw)) return;
                  const digits = raw.replace(/[^\d]/g, '');
                  const intVal = digits === '' ? 0 : parseInt(digits, 10);
                  setQuantidadeStr(digits === '' ? '' : String(intVal));
                  setQuantidade(intVal < 1 ? 0 : intVal);
                }
              }}
              onBlur={() => {
                if (!hasProduct) return;
                if (permiteDecimal) {
                  setQuantidadeStr(formatQuantidadeDecimalDigits(quantidadeDigits));
                  setQuantidade(parseQuantidadeDecimalDigits(quantidadeDigits));
                } else {
                  const intVal = Math.max(1, Math.floor(quantidade) || 1);
                  setQuantidade(intVal);
                  setQuantidadeStr(String(intVal));
                }
              }}
              onKeyDown={(e) => {
                if (!hasProduct || !permiteDecimal) {
                  handleKeyDown(e);
                  return;
                }
                if (/^\d$/.test(e.key)) {
                  e.preventDefault();
                  const digits = quantidadeDigits + e.key;
                  setQuantidadeDigits(digits);
                  setQuantidadeStr(formatQuantidadeDecimalDigits(digits));
                  setQuantidade(parseQuantidadeDecimalDigits(digits));
                  return;
                }
                if (e.key === 'Backspace') {
                  e.preventDefault();
                  const digits = quantidadeDigits.slice(0, -1);
                  setQuantidadeDigits(digits);
                  setQuantidadeStr(formatQuantidadeDecimalDigits(digits));
                  setQuantidade(parseQuantidadeDecimalDigits(digits));
                  return;
                }
                if (e.key === 'Delete') {
                  e.preventDefault();
                  setQuantidadeDigits('');
                  setQuantidadeStr('');
                  setQuantidade(0);
                  return;
                }
                if (e.key === '.' || e.key === ',' || e.key === '-' || e.key === '+') {
                  e.preventDefault();
                  return;
                }
                handleKeyDown(e);
              }}
              className="box-border min-h-[40px] w-full max-w-[5.5rem] min-w-0 justify-self-start rounded-md border border-zinc-600 bg-[#333333] px-2.5 py-2 text-sm text-white outline-none read-only:cursor-default read-only:opacity-80 focus:border-white focus:ring-1 focus:ring-white/80 lg:h-auto lg:max-w-none lg:min-h-0 lg:w-24 lg:flex-none lg:px-3 lg:py-2.5"
            />
          </div>

          <div className="grid w-full min-w-0 grid-cols-[1.25rem_6.75rem_minmax(0,1fr)] items-center gap-x-2 lg:flex lg:gap-2">
            <Banknote className="h-5 w-5 shrink-0 text-white" aria-hidden />
            <label
              htmlFor="caixa-produto-unit"
              className="truncate text-sm text-zinc-300 lg:w-28 lg:shrink-0"
            >
              Valor unitário:
            </label>
            <MoneyInput
              id="caixa-produto-unit"
              value={hasProduct ? precoUnitarioStr : ''}
              readOnly={!hasProduct}
              placeholder={hasProduct ? '0,00' : ''}
              onChange={(v) => hasProduct && setPrecoUnitarioStr(v)}
              onKeyDown={handleKeyDown}
              currencyPrefix
              inputClassName="px-2.5 py-2 lg:px-3 lg:py-2.5"
              className="box-border min-h-[40px] w-full max-w-[9.5rem] min-w-0 justify-self-start rounded-md border border-zinc-600 bg-[#333333] text-sm text-white outline-none read-only:cursor-default read-only:opacity-80 focus-within:border-white focus-within:ring-1 focus-within:ring-white/80 lg:max-w-none lg:min-h-[44px] lg:flex-1"
            />
          </div>

          <div className="grid w-full min-w-0 grid-cols-[1.25rem_6.75rem_minmax(0,1fr)] items-center gap-x-2 lg:flex lg:gap-2">
            <Coins className="h-5 w-5 shrink-0 text-white" aria-hidden />
            <label className="truncate text-sm text-zinc-300 lg:w-28 lg:shrink-0">Valor total:</label>
            <span className="caixa-money-input box-border min-h-[40px] w-full max-w-[9.5rem] min-w-0 justify-self-start rounded-md border border-zinc-600 bg-[#333333] text-sm text-white lg:max-w-none lg:min-h-[44px] lg:flex-1">
              <span className="caixa-money-input__prefix">R$</span>
              <span className="caixa-money-input__value flex items-center px-2.5 py-2 lg:px-3 lg:py-2.5">
                {hasProduct ? numberToMoneyMask(total) : null}
              </span>
            </span>
          </div>
        </div>

        {hasProduct && (
          <button
            type="button"
            onClick={tryAdd}
            disabled={disabled || (permiteDecimal ? quantidade <= 0 : quantidade < 1)}
            className="w-full min-h-[44px] shrink-0 rounded-md border border-zinc-300/90 bg-white px-4 py-3 text-sm font-medium text-zinc-900 shadow-sm transition-colors hover:bg-[#F3F4F6] active:bg-zinc-200 disabled:pointer-events-none disabled:opacity-50"
          >
            Adicionar
          </button>
        )}
      </div>
    );
  }
);
